/**
 * The camp verbs, as data for a library `Menu` (M3-04, retired to this shape in
 * RC2-07).
 *
 * This was `ui/camp.ts` and the camp's six verbs until M3-04; what changed then
 * was not the shape but **where the list comes from**. It used to work out what
 * was available itself, from a hand-written reading of the snapshot: no fire and
 * some wood means you can build, a fire and something raw means you can cook.
 * That was a second opinion about the rules, living a package away from the
 * rules, and it was wrong in the ordinary way second opinions are — it did not
 * know that a broken arm stops you laying a fire, because nothing told it, and
 * nothing was ever going to.
 *
 * Now the server sends `self.affordances`: every verb, whether it is available,
 * and the sentence saying why not. Those come from the same fault functions the
 * intent handlers call, so this menu and the rule it is a menu for cannot
 * disagree. There is nothing left in this file that decides anything.
 *
 * ## What RC2-07 retired
 *
 * The file used to carry a second thing: a `VerbMenu` state machine and a
 * `verbPrompt` that rendered the verbs as `lay a fire (b) · cook (c) — there is
 * no fire here`, with the game intercepting the next keystroke. That was the
 * bespoke menu, and it has been gone from the screen since RC1-05 put the
 * affordances into a focused `Popup`+`Menu`. Keeping a dead second renderer
 * around is how a menu and its prompt end up offering different verbs, so the
 * prompt and the machine are deleted and what is left is the *adapter*: the
 * key table, the labels, and the one resolution rule.
 *
 * Pure state. It knows nothing about the DOM and nothing about the socket.
 */

import type { MenuItem } from '@annwn/ui';

import type {
  AffordanceSnapshot,
  IntentType,
  NeedsSnapshot,
  SatchelSnapshot,
} from '@annwn/protocol';

/**
 * The keys, in the order the prompt lists them.
 *
 * Bound per verb rather than assigned by position, so that a verb keeps its key
 * when the one above it becomes unavailable — a menu whose letters move as the
 * world changes is a menu nobody can build muscle memory for.
 *
 * `x` for finish is inherited from M2-07 along with its reason: it is the
 * destructive one, it is last, and it is nowhere near the finger that just
 * opened the menu.
 */
export const VERB_KEYS: readonly { readonly key: string; readonly verb: IntentType }[] = [
  { key: 'g', verb: 'forage' },
  { key: 'b', verb: 'build' },
  { key: 'k', verb: 'kindle' },
  { key: 'f', verb: 'feed' },
  { key: 'c', verb: 'cook' },
  { key: 'u', verb: 'butcher' },
  { key: 'e', verb: 'eat' },
  // M4-03. `p` for the preparations at a fire — a poultice, a brew.
  { key: 'p', verb: 'prepare' },
  { key: 'z', verb: 'rest' },
  // Self-care (M4). The same treatments reach a fallen ally through the interact
  // menu; here they act on your own wounds, which is why they sit among the
  // things you do to yourself rather than beside `loot`.
  { key: 's', verb: 'staunch' },
  { key: 'a', verb: 'bandage' },
  { key: 'w', verb: 'clean' },
  { key: 't', verb: 'suture' },
  { key: 'r', verb: 'cauterize' },
  // M4-05's surgery, on yourself. `i` for digging it out, `v` for the axe —
  // both away from the movement keys, and the amputation one deliberately not
  // beside anything anybody presses in a hurry.
  { key: 'i', verb: 'extract' },
  { key: 'v', verb: 'amputate' },
  // M4-06. Putting somebody down is a thing you do to yourself, like standing
  // up — it takes no target, because you are holding one thing or none.
  { key: 'o', verb: 'release' },
  // M4-07. Burial and its opposite, at the end with the other grave business:
  // both are spade work, and `n` is nowhere near anything anybody presses in a
  // hurry.
  { key: 'n', verb: 'bury' },
  { key: 'h', verb: 'dig' },
  { key: 'l', verb: 'loot' },
  { key: 'd', verb: 'drag' },
  // RC2-04's reward ladder, against the hazel deadfall. `y` to take the axe out
  // of it, `j` to chop the wood down — both ground verbs, kept away from the
  // movement keys like the rest of the camp labour.
  { key: 'y', verb: 'salvage' },
  { key: 'j', verb: 'chop' },
  // `execute` stays last and on the key M2-07 gave it — far from anything anyone
  // presses in a hurry (the test below holds it here).
  { key: 'x', verb: 'execute' },
];

const KEY_OF_VERB = new Map(VERB_KEYS.map((entry) => [entry.verb, entry.key]));

/** What the menu is being opened against — the last thing the server said. */
export interface VerbContext {
  readonly affordances: readonly AffordanceSnapshot[];
  readonly needs: NeedsSnapshot;
  readonly satchel: SatchelSnapshot;

  /** The body the server judged the `body` verbs against, if there was one. */
  readonly targetId?: string | null;
}

/** What the menu resolves to. `itemId` is set only for `eat`. */
export interface VerbChoice {
  readonly verb: IntentType;
  readonly itemId: string | null;

  /** For `rest`, which way the toggle is going. */
  readonly on: boolean;

  /** For the body verbs, who it is aimed at. */
  readonly targetId: string | null;
}

/**
 * What to eat, if the player chooses `eat`.
 *
 * The most perishable thing first — cooked before raw before anything that
 * keeps — because that is the order a person eats a satchel in, and because
 * leaving the bilberries to rot while you save them is a mistake the interface
 * should not encourage. Ties break by name, so the choice is stable.
 *
 * Still decided here rather than server-side, and it is the one such decision
 * left: the server's affordance says *whether* there is anything to eat, and
 * this says *which*, which is a preference rather than a rule. A player who
 * disagrees can open the satchel.
 */
export function mealFor(satchel: SatchelSnapshot): string | null {
  const edible = satchel.items.filter((item) => item.edible);
  if (edible.length === 0) return null;

  const rank = (freshness: string | null): number =>
    freshness === 'old' ? 0 : freshness === 'wilting' ? 1 : freshness === 'fresh' ? 2 : 3;

  const best = [...edible].sort(
    (left, right) =>
      rank(left.freshness) - rank(right.freshness) || left.name.localeCompare(right.name),
  )[0];

  return best?.defId ?? null;
}

/** The word for a verb in this context. Only `rest` reads differently both ways. */
function labelFor(offer: AffordanceSnapshot, context: VerbContext): string {
  if (offer.verb === 'rest' && context.needs.resting) return 'get up';
  return offer.label;
}

/**
 * The window title for the same menu as a `Popup` (RC1-05) — "At the fire" when
 * there is one, because that is the fact that changes what the menu is for.
 */
export function actionMenuTitle(context: VerbContext): string {
  return context.needs.fire ? 'At the fire' : 'Here';
}

/**
 * The affordances as `@annwn/ui` `MenuItem`s (RC1-05).
 *
 * One list, in the server's order, carrying the same letters the keyboard uses —
 * so the row a mouse clicks and the key a hand presses cannot offer different
 * verbs. An available verb carries its note (how well it would go) as the meta;
 * a refused one is greyed and carries its reason in place of the meta, never
 * hidden. Nothing here decides anything.
 */
export function actionMenuItems(context: VerbContext): MenuItem[] {
  return context.affordances.map((offer): MenuItem => {
    const base = {
      id: offer.verb,
      name: labelFor(offer, context),
      ...(KEY_OF_VERB.get(offer.verb) ? { key: KEY_OF_VERB.get(offer.verb) } : {}),
    };
    if (offer.available) {
      return offer.note ? { ...base, meta: offer.note } : base;
    }
    return { ...base, disabled: true, disabledReason: offer.reason ?? 'not now' };
  });
}

/**
 * Resolve a chosen verb against the last snapshot.
 *
 * Reached by *verb* rather than by keystroke, which is what lets the single-key
 * select inside the `Menu`, a click on a row, and the tile context menu all
 * arrive at one rule instead of three.
 */
export function resolveVerbChoice(
  context: VerbContext,
  verb: IntentType,
): { choice: VerbChoice } | { refused: string } | null {
  const offer = context.affordances.find((candidate) => candidate.verb === verb);
  if (!offer) return null;
  if (!offer.available) return { refused: offer.reason ?? 'not now' };

  const targetId = offer.target === 'body' ? (context.targetId ?? null) : null;

  if (verb === 'eat') {
    const itemId = mealFor(context.satchel);
    return itemId === null
      ? { refused: 'you have nothing to eat' }
      : { choice: { verb, itemId, on: false, targetId } };
  }

  if (verb === 'rest') {
    return { choice: { verb, itemId: null, on: !context.needs.resting, targetId } };
  }

  return { choice: { verb, itemId: null, on: false, targetId } };
}
